import React from 'react';
import styled from 'styled-components';
import {
  StyledContentWrapper,
  StyledPageContainer,
  StyledBody,
  StyledHeaderWrapper,
} from 'components/Page/StyledComponents';
import { Title, theme, Text, Switch, Button, Link, Tooltip } from 'components';
import type { JsonObject } from '@/app-package';
import { useBreakpoint } from 'hooks';
import { buildCssProp } from 'utils';
import OverAlert from 'components/PageComponents/AlertComponents/OverAlert';
import { ListingDoc } from '@bb/domain/dist/schemas';

export type FeedbackSettingsLayoutProps = {
  listing: JsonObject<ListingDoc>;
  showWarning: () => void;
  feedbackCollecting: boolean;
  saveSettings: () => void;
  hasLimitedAccess?: boolean;
  isInvalidSurvey?: boolean;
};

const FeedbackSettingsLayout: React.FC<FeedbackSettingsLayoutProps> = ({
  children,
  listing,
  showWarning,
  saveSettings,
  feedbackCollecting,
  hasLimitedAccess,
  isInvalidSurvey,
}) => {
  const { md } = useBreakpoint();
  return (
    <StyledPageContainer>
      <StyledHeaderWrapper $elevated={false}>
        {hasLimitedAccess && (
          <StyledOverAlertWrapper>
            <OverAlert variant="noAccess" />
          </StyledOverAlertWrapper>
        )}
        <StyledPageHeader $md={md}>
          <div>
            <Link
              href={`#/listing/${listing?.id}/view`}
              data-cy="listing-view-link"
            >
              <StyledTitle level={3} $color={theme.primary7} inline>
                {listing?.line1} {listing?.line2 || ''}
              </StyledTitle>
            </Link>

            <StyledTitle
              inline
              level={3}
              $color={theme.primary10}
              $fontWeight={500}
            >
              {' '}
              - Feedback Settings
            </StyledTitle>
          </div>
          <StyledHeaderExtraWrapper>
            <Switch
              checked={feedbackCollecting}
              onChange={showWarning}
              disabled={hasLimitedAccess}
              data-cy="feedback-settings-toggle-collect"
            />{' '}
            <Text>Collecting Feedback</Text>
            <Button
              href={`#/listing/${listing?.id}/showings`}
              disabled={hasLimitedAccess}
              data-cy="back-to-showings-button"
            >
              Cancel
            </Button>
            <Tooltip
              placement="bottomLeft"
              title={
                isInvalidSurvey
                  ? 'Must include at least one survey question'
                  : ''
              }
            >
              <div>
                <StyledButton
                  type="primary"
                  onClick={saveSettings}
                  disabled={hasLimitedAccess || isInvalidSurvey}
                  $disabled={hasLimitedAccess || isInvalidSurvey}
                  data-cy="feedback-settings-save-button"
                >
                  Save
                </StyledButton>
              </div>
            </Tooltip>
          </StyledHeaderExtraWrapper>
        </StyledPageHeader>
      </StyledHeaderWrapper>
      <StyledContentWrapper
        $hasAside={false}
        $asidePlacement={'left'}
        $asideWidth={undefined}
      >
        {/** padding fix */}
        <StyledBody $padding={0} $height={'500px'} $backgroundColor={'white'}>
          {children}
        </StyledBody>
      </StyledContentWrapper>
    </StyledPageContainer>
  );
};

const StyledPageHeader = styled.div<{ $md: boolean }>`
  padding: 0px 20px;
  background: white;
  display: flex;
  align-items: center;
  flex-direction: ${({ $md }) => ($md ? 'row' : 'column')};
  justify-content: space-between;
`;

const StyledHeaderExtraWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0 10px;
`;

const StyledOverAlertWrapper = styled.div`
  padding: 8px 20px;
  background: ${theme.white};
`;

const StyledTitle = styled(Title)<{ $color: string; $fontWeight?: number }>`
  color: ${({ $color }) => $color} !important;
  ${({ $fontWeight }) =>
    $fontWeight ? `font-weight: ${$fontWeight} !important;` : ''}
`;

const StyledButton = styled(Button)<{ $disabled?: boolean }>`
  ${({ $disabled }) => buildCssProp('color', !$disabled && 'white !important')}
`;

export default FeedbackSettingsLayout;
